import { ReactNode } from 'react';
import { Box, Link, Skeleton, Typography } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { Change } from '../dashboard-filters';
import { percent, useDashboardColors } from './panel';

interface Props {
    label: string;
    value?: ReactNode;
    unit?: string;
    hint?: string;
    /// Variação contra o período anterior. Sem ela o cartão é foto de agora.
    change?: Change;
    /// Descrição do período comparado ("vs. 28/07 – 26/08").
    compareLabel?: string;
    /// Subir é bom? Para indicador de problema, subir é piora.
    higherIsBetter?: boolean;
    attention?: boolean;
    linkLabel?: string;
    onLink?: () => void;
}

/// Número em destaque com o "subiu ou caiu" abaixo; a palavra repete a cor, para não depender só dela.
export const KpiCard = ({
    label, value, unit, hint, change, compareLabel, higherIsBetter = true, attention, linkLabel, onLink,
}: Props) => {
    const { t } = useTranslation();
    const cc = useDashboardColors();

    const good = change && change.direction !== 'flat' && ((change.direction === 'up') === higherIsBetter);
    const tone = !change || change.direction === 'flat' ? cc.textSecondary : good ? cc.eventSuccess : cc.eventDanger;
    const arrow = change?.direction === 'up' ? '↑' : change?.direction === 'down' ? '↓' : '→';

    return (
        <Box
            sx={{
                background: cc.cardBg,
                border: `1px solid ${cc.border}`,
                borderRadius: 3,
                p: 2.5,
                display: 'flex',
                flexDirection: 'column',
                gap: 0.4,
                minWidth: 0,
            }}
        >
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Typography sx={{ color: cc.textPrimary, fontWeight: 600, fontSize: '0.82rem', flex: 1 }}>
                    {label}
                </Typography>
                {attention && (
                    <Typography
                        sx={{
                            color: cc.eventDanger,
                            border: `1px solid ${cc.eventDanger}`,
                            borderRadius: '5px',
                            px: 0.6,
                            fontSize: '0.62rem',
                            fontWeight: 700,
                            letterSpacing: '.04em',
                        }}
                    >
                        ! {t('painel.atencao')}
                    </Typography>
                )}
            </Box>

            {value === undefined ? (
                <Skeleton width={110} height={44} />
            ) : (
                <Typography
                    sx={{ color: cc.textPrimary, fontWeight: 700, fontSize: '1.9rem', lineHeight: 1.15, fontVariantNumeric: 'tabular-nums' }}
                >
                    {value}
                    {unit && (
                        <Box component="span" sx={{ color: cc.textSecondary, fontWeight: 500, fontSize: '0.8rem', ml: 0.8 }}>
                            {unit}
                        </Box>
                    )}
                </Typography>
            )}

            {hint && (
                <Typography variant="caption" sx={{ color: cc.textSecondary, fontSize: '0.74rem' }}>
                    {hint}
                </Typography>
            )}

            {(change || linkLabel) && (
                <Box sx={{ mt: 'auto', pt: 1.2, borderTop: `1px solid ${cc.divider}`, display: 'grid', gap: 0.6 }}>
                    {change && (
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                            <Typography
                                sx={{
                                    color: tone,
                                    background: `${tone}1f`,
                                    borderRadius: '5px',
                                    px: 0.7,
                                    fontSize: '0.72rem',
                                    fontWeight: 700,
                                    fontVariantNumeric: 'tabular-nums',
                                }}
                            >
                                {arrow}{' '}
                                {change.percent === null
                                    ? t('painel.novo')
                                    : `${change.percent > 0 ? '+' : ''}${percent(change.percent)}`}
                                {change.direction !== 'flat' && ` · ${good ? t('painel.melhora') : t('painel.piora')}`}
                            </Typography>
                            {compareLabel && (
                                <Typography sx={{ color: cc.textMuted, fontSize: '0.7rem' }}>{compareLabel}</Typography>
                            )}
                        </Box>
                    )}
                    {linkLabel && onLink && (
                        <Link
                            component="button"
                            type="button"
                            onClick={onLink}
                            underline="hover"
                            sx={{ color: cc.accent, fontSize: '0.76rem', fontWeight: 600, textAlign: 'left' }}
                        >
                            {linkLabel} →
                        </Link>
                    )}
                </Box>
            )}
        </Box>
    );
};
